import { Upload, Button, Typography, Progress } from "antd";
import { UploadOutlined, DeleteOutlined, FileOutlined } from "@ant-design/icons";
import { FileDetails, FileImage, UploadContainer } from "./styles";
import { ExtensionList, FileUploadComponentProps, MAX_VISIBLE_FILES } from "./constants";
import { useFileUpload } from "./useFileUpload";
import { useMemo } from "react";
export type { FileData } from "./constants";

// File Upload Component
const FileUploadComponent = ({
	title,
	style,
	placeholder = "Dosya Seçiniz",
	maxFileSize,
	allowedExtensions,
	defaultUrl,
	dimensions,
	multiple = false,
	maxFileCount,
	isRequired = false,
	isDisabled = false,
	onChange,
	onError,
}: FileUploadComponentProps) => {
	const {
		fileList,
		defaultFile,
		customUploadAsync,
		uploadProgress,
		allowedMimeTypes,
		handleRemove,
		handleRemoveAll
	} = useFileUpload({ maxFileSize, multiple, maxFileCount, defaultUrl, allowedExtensions, dimensions, onChange, onError });

	// Maximum file size in MB for display purposes
	const maxFileSizeInMB = useMemo(() => {
		const bytes = Number(maxFileSize);
		if (!maxFileSize || Number.isNaN(bytes) || bytes < 0) {
			return null;
		}
		return (bytes / (1024 * 1024)).toFixed(2);
	}, [maxFileSize]);

	return (
		<UploadContainer style={style}>
			{title && (
				<Typography.Title level={5} style={{ color: "#2e333e" }}>
					{title}
					{isRequired && <span style={{ color: "red" }}>&nbsp;*</span>}
				</Typography.Title>
			)}
			<Typography.Paragraph>
				Maksimum dosya boyutu: {maxFileSizeInMB !== null ? `${maxFileSizeInMB} MB` : "Bilinmiyor"}
				<br />Desteklenen formatlar: {allowedExtensions.join(", ").toUpperCase()}
				{multiple && typeof maxFileCount === "number" && maxFileCount > 0 && <><br /> Maksimum dosya adedi: {maxFileCount}</>}
				{allowedExtensions.every(ext => (ExtensionList.Image as readonly string[]).includes(ext)) && dimensions && dimensions.length > 0 && (
					<><br /> Önerilen boyut: {dimensions.map(d => `${d.width}x${d.height}`).join(", ")} px</>
				)}
			</Typography.Paragraph>
			<Upload
				beforeUpload={async (file, batchFileList) => {
					await customUploadAsync(batchFileList, file);
					return false;
				}}
				disabled={isDisabled}
				fileList={[]}
				showUploadList={false}
				multiple={multiple}
				accept={allowedMimeTypes.join(",")}
			>
				<Button
					icon={<UploadOutlined />}
					size="large"
					disabled={isDisabled}
				>
					{placeholder}
				</Button>
			</Upload>

			{uploadProgress > 0 && uploadProgress < 100 && (
				<Progress percent={uploadProgress} />
			)}

			{fileList.length === 0 && defaultFile && (
				<FileDetails>
					{defaultFile.type.startsWith("image/") ? (
						<FileImage src={defaultFile.url} alt={`Önizleme: ${defaultFile.name}`} />
					) : (
						<div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
							<FileOutlined style={{ fontSize: 24, color: "#595959" }} />
							<Typography.Link
								href={defaultFile.url}
								target="_blank"
								rel="noopener noreferrer"
								ellipsis
								style={{ maxWidth: 220 }}
							>
								{defaultFile.name}
							</Typography.Link>
						</div>
					)}
				</FileDetails>
			)}
			{fileList.length >= MAX_VISIBLE_FILES && (
				<FileDetails>
					<div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8, marginTop: 10 }}>
						<FileOutlined style={{ fontSize: 32, color: "#595959" }} />
						<Typography.Text strong>{fileList.length} dosya yüklendi</Typography.Text>
					</div>
					<Button
						icon={<DeleteOutlined />}
						type="primary"
						danger
						onClick={handleRemoveAll}
						style={{ marginTop: "auto" }}
						aria-label={`Tüm dosyaları sil (${fileList.length} dosya)`}
					>
						Tümünü Sil
					</Button>
				</FileDetails>
			)}

			{fileList.length > 0 && fileList.length < MAX_VISIBLE_FILES && (
				<>
					{fileList.map((fileInfo) => (
						<FileDetails key={fileInfo.id}>
							{fileInfo.type.startsWith("image/") ? (
								<FileImage src={fileInfo.url as string} alt={`Önizleme: ${fileInfo.name}`} />
							) : (
								<div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
									<FileOutlined style={{ fontSize: 24, color: "#595959" }} />
									<Typography.Link
										href={typeof fileInfo.url === "string" ? fileInfo.url : undefined}
										target="_blank"
										rel="noopener noreferrer"
										ellipsis
										style={{ maxWidth: 220 }}
									>
										{fileInfo.name}
									</Typography.Link>
								</div>
							)}
							<Button
								icon={<DeleteOutlined />}
								type="primary"
								danger
								onClick={() => handleRemove(fileInfo)}
								style={{ marginTop: "auto" }}
								aria-label={`${fileInfo.name} dosyasını sil`}
							>
								Dosyayı Sil
							</Button>
						</FileDetails>
					))}
				</>
			)}
		</UploadContainer>
	);
};

export default FileUploadComponent;
